import { useCallback, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { NewReviTherapyApi } from "../../../../../api/revi";
import { useTherapyMutations } from "../../../../../hooks/revi/useTherapyMutations";

const therapyApi = new NewReviTherapyApi();

export function useTherapyDetail({ accessToken, onAfterChange } = {}) {
  const { t } = useTranslation("therapist");
  const [currentTherapyDetail, setCurrentTherapyDetail] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const handleViewTherapy = useCallback(
    async (therapySummary) => {
      if (!accessToken || !therapySummary?._id) return;

      setIsDetailModalOpen(true);
      setDetailLoading(true);
      try {
        const fullTherapy = await therapyApi.getTherapyById(
          accessToken,
          therapySummary._id,
        );
        setCurrentTherapyDetail(fullTherapy);
      } catch (e) {
        console.error(e);
        message.error(t("salas.avisos.errorCargarDetalle"));
        setIsDetailModalOpen(false);
        setCurrentTherapyDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [accessToken, t],
  );

  const closeDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setCurrentTherapyDetail(null);
  }, []);

  const handleRemoveStep = useCallback(
    async (stepId) => {
      if (!accessToken || !currentTherapyDetail?._id) return;

      try {
        await therapyApi.removeSceneStep(
          accessToken,
          currentTherapyDetail._id,
          stepId,
        );

        message.success(t("salas.avisos.sesionEliminada"));
        setCurrentTherapyDetail((prev) => ({
          ...prev,
          scenes: (prev?.scenes || []).filter((s) => s._id !== stepId),
        }));

        onAfterChange?.();
      } catch (e) {
        console.error(e);
        message.error(t("salas.avisos.errorEliminarSesion"));
      }
    },
    [accessToken, currentTherapyDetail?._id, onAfterChange, t],
  );

  // ✅ NUEVO: mover step arriba (swap + persistencia)
  const handleMoveStepUp = useCallback(
    async (stepId) => {
      if (!accessToken || !currentTherapyDetail?._id || !stepId) return;

      const therapyId = currentTherapyDetail._id;
      const prevScenes = currentTherapyDetail.scenes || [];
      const idx = prevScenes.findIndex((s) => s._id === stepId);

      if (idx <= 0) return; // si es 0 o no existe -> nada

      // 1) Optimista: swap en UI
      const swapped = [...prevScenes];
      const tmp = swapped[idx - 1];
      swapped[idx - 1] = swapped[idx];
      swapped[idx] = tmp;

      setCurrentTherapyDetail((prev) => ({ ...prev, scenes: swapped }));

      // 2) Persistencia: PATCH backend
      try {
        const baseApi = therapyApi.baseApi || therapyApi.baseURL || "";
        if (!baseApi) {
          throw new Error("No se pudo resolver baseApi en NewReviTherapyApi");
        }

        const url = `${baseApi}/revi/therapy/${therapyId}/scene/${stepId}/move-up`;

        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        const json = await res.json();
        if (res.status !== 200) {
          // El backend manda su propio mensaje; solo se traduce el respaldo.
          throw new Error(json.message || t("salas.avisos.errorReordenar"));
        }

        // Si backend devuelve terapia hidratada, usamos esa
        const fresh = await therapyApi.getTherapyById(accessToken, therapyId);
        setCurrentTherapyDetail(fresh);

        onAfterChange?.();
      } catch (e) {
        console.error(e);
        message.error(e.message || t("salas.avisos.errorReordenar"));

        // rollback/refetch
        try {
          const fresh = await therapyApi.getTherapyById(accessToken, therapyId);
          setCurrentTherapyDetail(fresh);
        } catch (_) {
          setCurrentTherapyDetail((prev) => ({ ...prev, scenes: prevScenes }));
        }
      }
    },
    [accessToken, currentTherapyDetail, onAfterChange, t],
  );

  const { remove } = useTherapyMutations({
    onSuccess: () => {
      onAfterChange?.();
      if (isDetailModalOpen) closeDetailModal();
    },
  });

  const handleDeleteTherapy = useCallback(
    (therapy) => {
      remove(therapy);
    },
    [remove],
  );

  return {
    currentTherapyDetail,
    isDetailModalOpen,
    detailLoading,

    handleViewTherapy,
    closeDetailModal,
    handleRemoveStep,
    handleMoveStepUp, // ✅ EXPORT

    handleDeleteTherapy,
  };
}
